// State-wise Rent Act FAQ data.
//
// IMPORTANT — READ BEFORE EDITING:
// This data is INFORMATIONAL ONLY. It is not legal advice. The exact
// applicability of any rent control act depends on the date of tenancy,
// the rent amount, the locality (some acts only apply in notified urban
// areas), and the agreement terms. The UI MUST display the disclaimer
// from `LEGAL_DISCLAIMER` below alongside any answer.
//
// Coverage strategy:
//   - Phase 1 (covered now): TN, Maharashtra, Karnataka, Delhi, Telangana,
//     Andhra Pradesh, Kerala, West Bengal, Gujarat, Uttar Pradesh.
//   - Falls back to GENERIC_FAQ when a state has no entry yet, so adding
//     a state is purely additive — no UI change required.
//
// Schema per state:
//   actName            : full statute name (string)
//   actYear            : year enacted (number)
//   isMTAStyle         : whether the state has adopted Model Tenancy Act 2021 (bool)
//   depositCapMonths   : { residential: number|null, commercial: number|null }
//                        null = no statutory cap; "as per agreement" applies
//   noticePeriodDays   : { monthly: number, fixed: string }
//   registrationRequired: boolean (true = mandatory by statute)
//   rentIncreaseRule   : human-readable string
//   keyEvictionGrounds : array of strings
//   faqs               : array of { q, a | aLandlord | aTenant }
//                        - `a` used when answer is identical for both roles
//                        - `aLandlord` / `aTenant` for role-specific framing
//   helpfulLinks       : array of { label, url }  (optional)
//
// All facts here should be sourced to the bare statute or government
// portal. When the statute is silent or rules differ by city, the answer
// must say so explicitly — do NOT invent a number.

export const LEGAL_DISCLAIMER =
    "This information is for general guidance only and is not legal advice. " +
    "Rent control rules change frequently and vary by city, rent amount, and " +
    "tenancy date. For your specific situation, consult a qualified advocate.";

// Common labels used by every state's eviction-grounds list. Keeps wording
// consistent across the FAQ so users see the same vocabulary regardless
// of which state they're reading.
const EG = {
    NON_PAYMENT: "Non-payment of rent (usually 2+ months)",
    SUBLET: "Sub-letting without landlord's written consent",
    ILLEGAL_USE: "Using the premises for illegal or non-residential purposes",
    NUISANCE: "Causing nuisance / damage to the property",
    BONA_FIDE: "Landlord's bona fide need (own occupation by family)",
    DEMOLITION: "Demolition or major reconstruction",
    BREACH: "Material breach of agreement terms",
    EXPIRY: "Expiry of tenancy term and refusal to vacate",
};

// Generic safety-net FAQ used when the user's state has no specific entry.
// Phrased to avoid state-specific assertions.
const GENERIC_FAQ = {
    actName: "Your State's Rent Control Act + Model Tenancy Act 2021 (as adopted)",
    actYear: null,
    isMTAStyle: false,
    depositCapMonths: { residential: null, commercial: null },
    noticePeriodDays: { monthly: 30, fixed: "as per agreement" },
    registrationRequired: true, // Registration Act 1908 mandates registration for leases > 11 months everywhere
    rentIncreaseRule: "As per your written agreement. If silent, mutually agreed.",
    keyEvictionGrounds: [EG.NON_PAYMENT, EG.SUBLET, EG.ILLEGAL_USE, EG.NUISANCE, EG.BONA_FIDE, EG.BREACH],
    faqs: [
        {
            q: "Is a written rental agreement mandatory?",
            aLandlord: "Strongly recommended. Under the Registration Act 1908, leases longer than 11 months must be registered. Most states now also require even short-term tenancies to be in writing under their adopted Model Tenancy Act.",
            aTenant: "Yes, insist on a written agreement. An oral tenancy is hard to prove and weakens your protection if a dispute arises. If your tenancy is longer than 11 months, it should be registered.",
        },
        {
            q: "How much security deposit can be demanded?",
            aLandlord: "Most states do not yet have a statutory cap on residential deposits, so it is governed by your agreement. The Model Tenancy Act recommends a maximum of 2 months for residential and 6 months for commercial.",
            aTenant: "Check your state's act — many don't cap it yet. As a benchmark, the Model Tenancy Act (the central template) recommends max 2 months for residential. Anything significantly above that is unusual.",
        },
        {
            q: "What notice period applies to end the tenancy?",
            a: "Under Section 106 of the Transfer of Property Act 1882, monthly tenancies require 15 days' notice and yearly tenancies require 6 months — unless your written agreement says otherwise. Most modern agreements specify 1–3 months.",
        },
        {
            q: "Who pays for repairs and maintenance?",
            aLandlord: "Major structural repairs (roof, walls, plumbing leaks, electrical wiring) are the landlord's responsibility. Day-to-day upkeep (light bulbs, tap washers, cleaning) is on the tenant.",
            aTenant: "You handle minor day-to-day fixes (bulbs, washers, paint touch-ups). Major repairs — leaks, structural issues, electrical wiring — are the landlord's duty under the Transfer of Property Act.",
        },
        {
            q: "Can the landlord increase rent during the tenancy?",
            aLandlord: "Only as per your agreement. If the agreement is silent, you cannot unilaterally raise rent mid-term. Most landlords build in an annual escalation clause (typically 5–10%).",
            aTenant: "No, not unilaterally. Rent can only increase as per your written agreement. If your agreement is silent, the landlord cannot raise rent until renewal.",
        },
        {
            q: "What are the legal grounds for eviction?",
            aLandlord: "Common grounds across states: non-payment of rent (usually 2+ months), sub-letting without consent, illegal use, nuisance, bona fide need, expiry of term. Eviction must go through the Rent Authority / Civil Court — never by force or by cutting utilities.",
            aTenant: "A landlord cannot evict you informally. Even if you owe rent, the landlord must give written notice, then approach the Rent Authority or Civil Court. Cutting electricity / water / changing locks to force you out is illegal across India.",
        },
        {
            q: "Are rent receipts mandatory?",
            a: "Yes. Tenants are entitled to receipts for every rent payment. Receipts are required to claim HRA tax exemption (for rent above ₹3,000/month in salary) and are useful evidence in any dispute.",
        },
        {
            q: "Where do I go if there is a dispute?",
            a: "First approach the State Rent Authority (in MTA-adopted states) or the Rent Controller / Civil Court (in older-act states). Consumer Court is generally not the right forum for tenancy disputes. Always send a written notice before filing.",
        },
    ],
    helpfulLinks: [
        { label: "Model Tenancy Act 2021 (PDF)", url: "https://mohua.gov.in/upload/whatsnew/60639b07e6204Final-Notified_MTA_03022021.pdf" },
    ],
};

// ════════════════════════════════════════════════════════════════════
// STATE-SPECIFIC ENTRIES
// ════════════════════════════════════════════════════════════════════

const STATES = {
    "Tamil Nadu": {
        actName: "Tamil Nadu Regulation of Rights and Responsibilities of Landlords and Tenants Act, 2017",
        actYear: 2017,
        isMTAStyle: true,
        depositCapMonths: { residential: null, commercial: null }, // Act doesn't cap; common practice is 2-3 months
        noticePeriodDays: { monthly: 15, fixed: "as per agreement" },
        registrationRequired: true,
        rentIncreaseRule: "As per the agreement. If silent, no unilateral increase mid-term.",
        keyEvictionGrounds: [EG.NON_PAYMENT, EG.SUBLET, EG.ILLEGAL_USE, EG.NUISANCE, EG.BONA_FIDE, EG.BREACH, EG.EXPIRY],
        faqs: [
            {
                q: "Is registration of the agreement mandatory in Tamil Nadu?",
                aLandlord: "Yes. Every tenancy agreement must be registered with the Rent Authority on the TN Tenancy Portal (tenancy.tn.gov.in) within 90 days of signing. Without registration, you lose access to the Rent Authority for dispute resolution.",
                aTenant: "Yes — and protect yourself by ensuring the landlord registers it. Registered agreements are admissible as evidence and give you direct access to the Rent Authority for fast dispute resolution.",
            },
            {
                q: "How much security deposit can my landlord ask for?",
                aLandlord: "The TN Act does not cap residential deposits. Common market practice is 2–3 months. Note that the Model Tenancy Act 2021 recommends a maximum of 2 months residential and 6 months commercial — many tenants now expect this.",
                aTenant: "The TN Act doesn't fix a cap, so it depends on your agreement. As reference, the central Model Tenancy Act recommends max 2 months. Demands of 6+ months for residential are negotiable.",
            },
            {
                q: "What is the notice period in Tamil Nadu?",
                aLandlord: "Whatever your agreement says. If the agreement is silent, the Transfer of Property Act gives 15 days' notice for monthly tenancies. Best practice: build in 1–2 months in your agreement.",
                aTenant: "Check your agreement first. If silent, 15 days' notice applies for a monthly tenancy under the Transfer of Property Act.",
            },
            {
                q: "Can rent be increased during the tenancy?",
                aLandlord: "Only per the escalation clause in your agreement. Without one, you cannot unilaterally increase rent until renewal. The TN Act does not impose a percentage cap.",
                aTenant: "Only as agreed in writing. If your agreement has no escalation clause, the landlord cannot raise rent until the agreement is renewed.",
            },
            {
                q: "Who handles repairs?",
                aLandlord: "You're responsible for major structural repairs — walls, roof, plumbing pipes, electrical wiring, fixed sanitary fittings. The tenant handles minor day-to-day maintenance.",
                aTenant: "You only pay for minor maintenance (light bulbs, tap washers, your own appliances). Anything structural — water seepage, wiring, plumbing — is the landlord's duty.",
            },
            {
                q: "What are the eviction rules?",
                aLandlord: "You must apply to the TN Rent Authority for eviction on grounds like non-payment, sub-letting, nuisance, illegal use, or bona fide need. The Rent Authority is required to dispose cases within 60 days. You cannot force eviction by cutting utilities or changing locks.",
                aTenant: "You can only be evicted through a written order from the Rent Authority. If your landlord cuts electricity, water, or changes locks to pressure you out, that's illegal — file a complaint immediately.",
            },
            {
                q: "Where do I file a complaint?",
                a: "Tamil Nadu has a dedicated online portal — tenancy.tn.gov.in — for both registration and dispute resolution. The Rent Authority is statutorily required to dispose disputes within 60 days.",
            },
            {
                q: "Are rent receipts mandatory?",
                a: "Yes. The landlord must issue a written or digital rent receipt for every payment. Tenants need receipts to claim HRA tax benefits.",
            },
        ],
        helpfulLinks: [
            { label: "TN Tenancy Portal (register & dispute)", url: "https://tenancy.tn.gov.in" },
            { label: "TN Rent Act 2017 (PDF)", url: "https://www.tn.gov.in" },
        ],
    },

    "Maharashtra": {
        actName: "Maharashtra Rent Control Act, 1999",
        actYear: 1999,
        isMTAStyle: false,
        depositCapMonths: { residential: null, commercial: null }, // Capped only via "leave & license" common practice
        noticePeriodDays: { monthly: 30, fixed: "as per agreement" },
        registrationRequired: true, // For Leave & License — mandatory under Maharashtra Rent Control Act + Registration Act
        rentIncreaseRule: "Standard 'leave & license' agreements include annual 5–10% escalation. Old tenancies under MRC Act 1999 have rent freezes via Standard Rent.",
        keyEvictionGrounds: [EG.NON_PAYMENT, EG.SUBLET, EG.ILLEGAL_USE, EG.NUISANCE, EG.BONA_FIDE, EG.DEMOLITION],
        faqs: [
            {
                q: "Is a Leave & License agreement different from a regular rental agreement?",
                aLandlord: "Yes — and it's strongly preferred in Maharashtra. A Leave & License (L&L) agreement avoids the strong tenancy protections of the MRC Act 1999 (which makes evicting old tenants extremely difficult). Most modern Maharashtra rentals use L&L. It must be registered.",
                aTenant: "In Maharashtra, most agreements you'll sign are 'Leave & License' rather than tenancy. Under L&L, you have weaker statutory protection than under the old Rent Control Act, but agreements are easier to enforce on both sides.",
            },
            {
                q: "Is registration mandatory?",
                aLandlord: "Yes. Leave & License agreements MUST be registered with the Sub-Registrar within 4 months of signing. Stamp duty + registration fee applies. You can register online at igrmaharashtra.gov.in. Penalty for non-registration is up to 5x the registration fee.",
                aTenant: "Insist on registration. An unregistered L&L is hard to enforce and you lose access to fast dispute resolution. Registration cost is shared per the agreement (commonly 50:50).",
            },
            {
                q: "How much security deposit?",
                aLandlord: "There is no statutory cap. Common Mumbai/Pune practice: 2–6 months residential, 6–12 months commercial. Sky-high deposits (10+ months) are increasingly resisted by tenants since the Model Tenancy Act 2021 recommended 2 months.",
                aTenant: "No statutory cap, but 2–3 months is becoming the norm in newer L&Ls. Demands of 6+ months for a flat are negotiable — the central MTA recommends max 2 months.",
            },
            {
                q: "Can the landlord raise rent every year?",
                aLandlord: "Only if the agreement contains an escalation clause. Standard L&L agreements include 5–10% annual increases. Without a clause, you cannot raise mid-term.",
                aTenant: "Only as per your written escalation clause. Standard 5–10% per year is common. If silent, no increase until renewal.",
            },
            {
                q: "What about old 'pagdi' tenancies?",
                a: "Tenancies created before 1999 may fall under the old MRC Act with 'standard rent' (frozen historical rent) and very strong tenant protection. Inheritable in many cases. If you've inherited or are dealing with one of these, get specialist legal advice — they are governed differently.",
            },
            {
                q: "Who pays for repairs?",
                aLandlord: "Landlord handles structural repairs and external/common-area maintenance. Tenant handles minor wear-and-tear inside the unit. Society maintenance charges are usually paid by the landlord but reimbursed by the tenant per the L&L agreement.",
                aTenant: "Major repairs (walls, plumbing, wiring, leakages) are the landlord's. You handle minor stuff. Society maintenance is typically paid by the landlord; whether you reimburse depends on your agreement.",
            },
            {
                q: "How is eviction handled?",
                aLandlord: "Under L&L, eviction follows the Specific Relief Act + L&L agreement terms — much faster than old-act tenancies. For old-act tenancies, eviction is via the Small Causes Court and can take years.",
                aTenant: "L&L tenants can be evicted by following the agreement and Specific Relief Act. Old-act tenants have very strong protection. In either case, the landlord cannot use force, cut utilities, or change locks.",
            },
            {
                q: "Where do I file a dispute?",
                a: "For Leave & License: Civil Court in Mumbai/Pune areas. For old-act tenancies: Small Causes Court (Mumbai) or Civil Court. Police can be approached only for criminal acts (forced eviction, theft of belongings).",
            },
        ],
        helpfulLinks: [
            { label: "Maharashtra IGR (e-registration)", url: "https://igrmaharashtra.gov.in" },
        ],
    },

    "Karnataka": {
        actName: "Karnataka Rent Act, 1999 (amended 2001)",
        actYear: 1999,
        isMTAStyle: false,
        depositCapMonths: { residential: null, commercial: null },
        noticePeriodDays: { monthly: 30, fixed: "as per agreement" },
        registrationRequired: true,
        rentIncreaseRule: "Karnataka Rent Act doesn't cap. Most Bengaluru agreements include 5–10% annual escalation clauses.",
        keyEvictionGrounds: [EG.NON_PAYMENT, EG.SUBLET, EG.ILLEGAL_USE, EG.NUISANCE, EG.BONA_FIDE, EG.BREACH],
        faqs: [
            {
                q: "Does the Karnataka Rent Act apply to my flat?",
                aLandlord: "Probably not. The Karnataka Rent Act 1999 EXCLUDES premises with monthly rent above ₹3,500 (in Bengaluru/Mysuru) — which today covers virtually all urban rentals. Most Karnataka tenancies are governed only by the Transfer of Property Act 1882 and your written agreement.",
                aTenant: "The Karnataka Rent Act usually does not apply if your rent is above ₹3,500/month — which means most flats in Bengaluru are governed only by your written agreement and the Transfer of Property Act.",
            },
            {
                q: "Is a written agreement mandatory?",
                aLandlord: "Yes. Always have a written rental agreement on stamp paper. Leases longer than 11 months must be registered with the Sub-Registrar (Registration Act 1908). Most Bengaluru landlords use 11-month agreements to avoid registration.",
                aTenant: "Insist on a written agreement on stamp paper. If your agreement is for 11 months or less, registration isn't mandatory; over 11 months it must be registered.",
            },
            {
                q: "How much security deposit is normal?",
                aLandlord: "Bengaluru is famous for 10-month deposits, but this practice has been criticized and is now frequently negotiated down. The central Model Tenancy Act recommends max 2 months. Demanding 10+ months is increasingly seen as unfair.",
                aTenant: "Bengaluru landlords historically asked for 10 months. This is one of the highest in India and is negotiable. The central Model Tenancy Act recommends max 2 months for residential — many newer landlords now accept 3–6.",
            },
            {
                q: "What notice period applies?",
                aLandlord: "Whatever your agreement says — usually 1–3 months. If silent, 15 days for monthly tenancies under TPA.",
                aTenant: "As per your agreement (commonly 1 month notice from either side). If silent, 15 days under the Transfer of Property Act.",
            },
            {
                q: "Can rent be increased annually?",
                aLandlord: "Only per the escalation clause in your agreement. Common Bengaluru practice is 5% per year, sometimes 10%. Cannot increase mid-term without a clause.",
                aTenant: "Only as per your written agreement. 5% per year is the most common Bengaluru clause. The landlord cannot raise it unilaterally during the term.",
            },
            {
                q: "Who pays for major repairs?",
                aLandlord: "Major structural repairs (water seepage, plumbing pipes, electrical wiring) are your responsibility under the Transfer of Property Act. Tenant handles minor day-to-day items.",
                aTenant: "Major structural and plumbing repairs are the landlord's duty. You only handle minor consumables (bulbs, switches, your own appliances).",
            },
            {
                q: "How can a landlord evict a tenant?",
                aLandlord: "Through Civil Court — by giving written notice (under Section 106 TPA), waiting out the notice period, then filing for eviction if tenant doesn't vacate. You cannot use force, cut electricity/water, or change locks.",
                aTenant: "Only through a court order. If the landlord cuts electricity, water, or changes locks to force you out, that's illegal — file a police complaint and a civil suit.",
            },
            {
                q: "Where do I file a dispute?",
                a: "For most Bengaluru rentals (above ₹3,500/month rent), disputes go to the Civil Court / Small Causes Court. The Karnataka Rent Controller has limited jurisdiction. Notice in writing is mandatory before filing.",
            },
        ],
        helpfulLinks: [
            { label: "Karnataka Kaveri (e-registration)", url: "https://kaveri.karnataka.gov.in" },
        ],
    },

    "Delhi": {
        actName: "Delhi Rent Control Act, 1958 (Delhi Rent Act 1995 passed but not notified)",
        actYear: 1958,
        isMTAStyle: false,
        depositCapMonths: { residential: null, commercial: null },
        noticePeriodDays: { monthly: 30, fixed: "as per agreement" },
        registrationRequired: true,
        rentIncreaseRule: "1958 Act has rent caps for old tenancies but is largely irrelevant for new rentals. Modern Delhi agreements use private terms.",
        keyEvictionGrounds: [EG.NON_PAYMENT, EG.SUBLET, EG.ILLEGAL_USE, EG.NUISANCE, EG.BONA_FIDE, EG.BREACH],
        faqs: [
            {
                q: "Does the Delhi Rent Control Act 1958 apply to my tenancy?",
                aLandlord: "Mostly no. The Delhi Rent Control Act 1958 only applies to premises with rent UP TO ₹3,500/month — which today covers almost no urban rental in Delhi NCR. Modern tenancies are governed by the Transfer of Property Act 1882 and your written agreement.",
                aTenant: "Probably not. The 1958 Act applies only to rents up to ₹3,500/month — almost no Delhi NCR flat falls under it today. Your tenancy is governed by your written agreement and the Transfer of Property Act.",
            },
            {
                q: "Is a written agreement mandatory?",
                aLandlord: "Yes. Always use a written agreement on stamp paper (Delhi: ₹100 stamp paper for rentals up to 12 months, higher for longer). Agreements over 11 months must be registered with the Sub-Registrar.",
                aTenant: "Always insist on a written agreement on stamp paper. Without one, you have no clear evidence of rent, deposit, or terms.",
            },
            {
                q: "How much security deposit is typical?",
                aLandlord: "Delhi standard is 2–3 months residential. Above that is unusual and increasingly resisted. Commercial typically 6 months. No statutory cap.",
                aTenant: "2–3 months is the Delhi norm for residential. Anything above 3 months is unusual and negotiable.",
            },
            {
                q: "What notice period applies?",
                a: "Per your written agreement (commonly 1 month either side). If silent, the Transfer of Property Act gives 15 days for monthly tenancies. Both sides should give written notice.",
            },
            {
                q: "Can the landlord raise rent during the term?",
                aLandlord: "Only as per your written escalation clause (commonly 8–10% annually in Delhi). Without a clause, you cannot raise mid-term.",
                aTenant: "Only as per your written escalation clause. Delhi norm is 8–10% per year at renewal. The landlord cannot raise mid-term without a clause.",
            },
            {
                q: "Who is responsible for repairs?",
                aLandlord: "Major structural repairs are yours. Tenants handle minor day-to-day fixes and their own appliances.",
                aTenant: "Major repairs (walls, plumbing, wiring, leakages) are the landlord's. You only do minor work.",
            },
            {
                q: "How does eviction work in Delhi?",
                aLandlord: "Send a legal notice (Section 106 TPA), wait 15 days / 1 month, then file in Civil Court. Cases can be slow — budget 1–3 years. You cannot use force, cut utilities, or change locks.",
                aTenant: "Only through a court order. If the landlord cuts your electricity, water, or changes locks, that's illegal under the IPC + Delhi Police Act — file a police complaint at the local station and pursue civil remedies.",
            },
            {
                q: "Where do I file a dispute?",
                a: "Senior Civil Judge (Civil Court) for most modern Delhi tenancies. The Rent Controller covers only old-act tenancies (rent up to ₹3,500). Always send a written legal notice first.",
            },
        ],
        helpfulLinks: [
            { label: "Delhi DORIS (e-registration)", url: "https://doris.delhigovt.nic.in" },
        ],
    },

    "Telangana": {
        actName: "Andhra Pradesh Buildings (Lease, Rent and Eviction) Control Act, 1960 (still in force in Telangana)",
        actYear: 1960,
        isMTAStyle: false,
        depositCapMonths: { residential: null, commercial: null },
        noticePeriodDays: { monthly: 30, fixed: "as per agreement" },
        registrationRequired: true,
        rentIncreaseRule: "Old AP Act doesn't cap modern rentals. Most Hyderabad agreements include 5–10% annual escalation.",
        keyEvictionGrounds: [EG.NON_PAYMENT, EG.SUBLET, EG.ILLEGAL_USE, EG.NUISANCE, EG.BONA_FIDE, EG.BREACH, EG.EXPIRY],
        faqs: [
            {
                q: "Which act applies to Hyderabad rentals?",
                aLandlord: "The 1960 AP Buildings Act still applies to Telangana but its rent-control provisions only cover specific notified urban areas with rent under specified thresholds. Modern Hyderabad apartments are mostly governed by the Transfer of Property Act and your written agreement.",
                aTenant: "The 1960 AP Act technically still applies but its strong rent-control provisions don't cover most modern Hyderabad rentals. Your tenancy mostly runs on the Transfer of Property Act + your written agreement.",
            },
            {
                q: "Is a written agreement mandatory?",
                aLandlord: "Always use one. Leases over 11 months must be registered with the Sub-Registrar. Stamp duty in Telangana is currently 0.5% of total rent + deposit (subject to change).",
                aTenant: "Always insist on a written agreement. 11-month agreements don't need registration; longer ones do.",
            },
            {
                q: "How much security deposit is normal?",
                aLandlord: "Hyderabad norm: 3–10 months residential (varies by locality). Gachibowli/Madhapur often see 6–10 months. No statutory cap.",
                aTenant: "Hyderabad deposits range from 3 to 10 months depending on locality. Higher in Gachibowli/Madhapur. Negotiate based on the central Model Tenancy Act benchmark of 2 months.",
            },
            {
                q: "What's the standard notice period?",
                a: "Per your written agreement, typically 1–2 months either side. If silent, the Transfer of Property Act gives 15 days for monthly tenancies.",
            },
            {
                q: "Can rent be increased mid-term?",
                aLandlord: "Only per your escalation clause. Without one, no unilateral increase. Standard is 5–10% per year.",
                aTenant: "Only as per your written agreement. Unilateral mid-term increases are not allowed.",
            },
            {
                q: "Who handles repairs?",
                aLandlord: "Structural repairs are the landlord's responsibility. Day-to-day items are the tenant's.",
                aTenant: "Structural / plumbing / wiring repairs are the landlord's. You handle minor consumables.",
            },
            {
                q: "How does eviction work?",
                aLandlord: "Through the Rent Controller (for old-act premises) or Civil Court. Written legal notice required, then formal proceedings. You cannot evict by force or cut utilities.",
                aTenant: "Only through a court / Rent Controller order. Cutting electricity / water / changing locks to evict you is illegal.",
            },
        ],
        helpfulLinks: [
            { label: "Telangana IGRS (e-registration)", url: "https://registration.telangana.gov.in" },
        ],
    },

    "Andhra Pradesh": {
        actName: "Andhra Pradesh Buildings (Lease, Rent and Eviction) Control Act, 1960",
        actYear: 1960,
        isMTAStyle: false,
        depositCapMonths: { residential: null, commercial: null },
        noticePeriodDays: { monthly: 30, fixed: "as per agreement" },
        registrationRequired: true,
        rentIncreaseRule: "Old AP Act doesn't cap modern rentals. Agreements typically include 5–10% annual escalation.",
        keyEvictionGrounds: [EG.NON_PAYMENT, EG.SUBLET, EG.ILLEGAL_USE, EG.NUISANCE, EG.BONA_FIDE, EG.BREACH, EG.EXPIRY],
        faqs: [
            {
                q: "Which act applies to AP rentals?",
                a: "The 1960 AP Buildings Act applies in notified urban areas (Vijayawada, Visakhapatnam, Tirupati, etc.) but its rent-control provisions cover only premises below specified rent thresholds. Most modern apartments are governed by the Transfer of Property Act and your written agreement.",
            },
            {
                q: "Is registration mandatory?",
                aLandlord: "Leases over 11 months must be registered. Stamp duty in AP is similar to Telangana (~0.5% of rent + deposit, subject to revision).",
                aTenant: "Yes for leases over 11 months. Registration gives stronger evidentiary value.",
            },
            {
                q: "How much security deposit?",
                aLandlord: "AP norm: 2–6 months residential. No statutory cap. Cyber-corridor areas (Vizag) see higher deposits.",
                aTenant: "AP deposits typically 2–6 months. Negotiate against the Model Tenancy Act benchmark of 2 months.",
            },
            {
                q: "Notice period?",
                a: "Per your written agreement (commonly 1 month). If silent, 15 days under TPA.",
            },
            {
                q: "Rent increase?",
                a: "Only per your escalation clause. 5–10% per year is standard. No unilateral mid-term hike.",
            },
            {
                q: "Repairs?",
                aLandlord: "Structural repairs are yours; day-to-day on the tenant.",
                aTenant: "Structural / plumbing / wiring on the landlord; minor items on you.",
            },
            {
                q: "Eviction?",
                a: "Through Rent Controller (old-act) or Civil Court. Written notice mandatory. Force/utility-cutting is illegal.",
            },
        ],
        helpfulLinks: [
            { label: "AP IGRS (e-registration)", url: "https://registration.ap.gov.in" },
        ],
    },

    "Kerala": {
        actName: "Kerala Buildings (Lease and Rent Control) Act, 1965",
        actYear: 1965,
        isMTAStyle: false,
        depositCapMonths: { residential: null, commercial: null },
        noticePeriodDays: { monthly: 30, fixed: "as per agreement" },
        registrationRequired: true,
        rentIncreaseRule: "Kerala Act allows fair-rent fixation by Rent Controller. Modern agreements typically include 5–10% annual escalation.",
        keyEvictionGrounds: [EG.NON_PAYMENT, EG.SUBLET, EG.ILLEGAL_USE, EG.NUISANCE, EG.BONA_FIDE, EG.DEMOLITION],
        faqs: [
            {
                q: "Is the Kerala Act tenant-friendly?",
                aLandlord: "Yes — historically very tenant-protective. Strong restrictions on eviction, fair-rent fixation by Rent Controller, and limited grounds for raising rent. Drafting a tight agreement is essential.",
                aTenant: "Yes. Kerala has one of India's more tenant-protective rent laws. Eviction grounds are limited and you can apply to the Rent Controller for fair-rent fixation if you believe rent is excessive.",
            },
            {
                q: "Is registration mandatory?",
                a: "Yes for leases over 11 months. Stamp duty applicable. Always insist on a written agreement irrespective of duration.",
            },
            {
                q: "Security deposit norms?",
                aLandlord: "Kerala norm: 1–3 months residential. Some cities (Kochi/TVM) see slightly higher in apartment complexes. No statutory cap.",
                aTenant: "Kerala has comparatively low deposits — 1–3 months is standard. Demands above 3 months are unusual.",
            },
            {
                q: "Can my landlord raise rent?",
                aLandlord: "Only as per the agreement. The Rent Controller can also fix 'fair rent' on application — typically pegged to property value.",
                aTenant: "Only per agreement. If you believe rent is excessive vs. property value, you can apply to the Rent Controller for fair-rent fixation under the 1965 Act.",
            },
            {
                q: "Notice period?",
                a: "Per agreement, typically 1 month. If silent, 15 days under TPA for monthly tenancies.",
            },
            {
                q: "Repairs?",
                aLandlord: "Structural repairs are yours. The Act allows the tenant to spend on repairs and deduct from rent if you neglect them after notice.",
                aTenant: "Structural repairs are the landlord's. If they refuse after written notice, the Act allows you to make the repair and deduct the cost from rent (with proper proof).",
            },
            {
                q: "Eviction grounds?",
                a: "Limited grounds: non-payment of 2+ months rent, sub-letting, nuisance, illegal use, bona fide need, demolition. Process goes through the Rent Control Court — not summary.",
            },
            {
                q: "Where to file a dispute?",
                a: "Rent Control Court (one in each district). Appeals go to the Rent Control Appellate Authority.",
            },
        ],
        helpfulLinks: [
            { label: "Kerala Registration Department", url: "https://keralaregistration.gov.in" },
        ],
    },

    "West Bengal": {
        actName: "West Bengal Premises Tenancy Act, 1997",
        actYear: 1997,
        isMTAStyle: false,
        depositCapMonths: { residential: null, commercial: null },
        noticePeriodDays: { monthly: 30, fixed: "as per agreement" },
        registrationRequired: true,
        rentIncreaseRule: "WB Act provides for biennial 5% increase if no escalation clause exists. Modern agreements override this.",
        keyEvictionGrounds: [EG.NON_PAYMENT, EG.SUBLET, EG.ILLEGAL_USE, EG.NUISANCE, EG.BONA_FIDE, EG.DEMOLITION],
        faqs: [
            {
                q: "Does the WB Act apply to my Kolkata tenancy?",
                aLandlord: "The WB Premises Tenancy Act 1997 applies to most urban tenancies in West Bengal, including Kolkata. It is notably more tenant-protective than newer state acts.",
                aTenant: "Yes — the 1997 Act covers most WB tenancies and gives you stronger statutory protection than tenants get in many other states.",
            },
            {
                q: "Is registration mandatory?",
                a: "Yes for leases over 12 months (Indian Registration Act). Stamp duty applies.",
            },
            {
                q: "Security deposit norms?",
                a: "Kolkata norm: 1–3 months residential. The Act doesn't cap, but practice is conservative compared to Mumbai/Bengaluru.",
            },
            {
                q: "Can rent be raised?",
                aLandlord: "If your agreement has no escalation clause, the 1997 Act allows a 5% increase every 2 years. With an agreement, the agreement governs.",
                aTenant: "If your agreement has no escalation, the law caps increase at 5% every 2 years. If your agreement specifies a higher rate, that controls — but unilateral mid-term increases are not allowed.",
            },
            {
                q: "Notice period?",
                a: "Per agreement, typically 1 month. If silent, 15 days under TPA.",
            },
            {
                q: "Repairs?",
                aLandlord: "Structural repairs are yours. The 1997 Act lets the tenant make essential repairs and deduct cost from rent if you fail to act after notice.",
                aTenant: "Structural repairs on the landlord. If the landlord doesn't act after written notice, the Act lets you spend up to a defined limit and deduct from rent.",
            },
            {
                q: "Eviction?",
                a: "Through the Rent Controller / Civil Court on limited grounds (non-payment of 4+ months for residential, sub-letting, nuisance, bona fide need). The 1997 Act is procedural-heavy — eviction takes time.",
            },
            {
                q: "Where to file a dispute?",
                a: "Rent Controller in your jurisdiction. Appeals to the Civil Judge / High Court.",
            },
        ],
        helpfulLinks: [
            { label: "WB e-District (registration)", url: "https://edistrict.wb.gov.in" },
        ],
    },

    "Gujarat": {
        actName: "Gujarat Rent Act, 2011",
        actYear: 2011,
        isMTAStyle: false,
        depositCapMonths: { residential: null, commercial: null },
        noticePeriodDays: { monthly: 30, fixed: "as per agreement" },
        registrationRequired: true,
        rentIncreaseRule: "Gujarat Act allows agreement-based escalation. Standard 5–10% annually.",
        keyEvictionGrounds: [EG.NON_PAYMENT, EG.SUBLET, EG.ILLEGAL_USE, EG.NUISANCE, EG.BONA_FIDE, EG.BREACH, EG.EXPIRY],
        faqs: [
            {
                q: "Is Gujarat's rent law modern?",
                a: "Yes — the 2011 Act is among India's more business-friendly rent statutes. It de-controls newly-let premises and lets parties contract freely.",
            },
            {
                q: "Is registration mandatory?",
                a: "Yes for leases over 11 months. Stamp duty applicable per Bombay Stamp Act (as adopted by Gujarat).",
            },
            {
                q: "Security deposit norms?",
                aLandlord: "Ahmedabad/Surat/Rajkot norm: 2–6 months residential. Higher in business districts. No statutory cap.",
                aTenant: "Gujarat norm 2–6 months. Negotiable above 3 months. Insist on a deposit-refund clause.",
            },
            {
                q: "Notice period?",
                a: "Per agreement (commonly 1–2 months). If silent, 15 days under TPA.",
            },
            {
                q: "Rent increase?",
                a: "Per your agreement. Standard 5–10% per year. Without a clause, no mid-term hike.",
            },
            {
                q: "Repairs?",
                aLandlord: "Structural repairs are yours. Day-to-day items on the tenant.",
                aTenant: "Structural repairs on the landlord. You handle minor items.",
            },
            {
                q: "Eviction?",
                a: "Through the Civil Court. Written legal notice mandatory. Force or utility-cutting is illegal.",
            },
        ],
        helpfulLinks: [
            { label: "Gujarat Garvi (e-registration)", url: "https://garvi.gujarat.gov.in" },
        ],
    },

    "Uttar Pradesh": {
        actName: "Uttar Pradesh Regulation of Urban Premises Tenancy Act, 2021 (MTA-aligned)",
        actYear: 2021,
        isMTAStyle: true,
        depositCapMonths: { residential: 2, commercial: 6 },
        noticePeriodDays: { monthly: 30, fixed: "as per agreement" },
        registrationRequired: true,
        rentIncreaseRule: "Per agreement. Act doesn't impose a percentage cap.",
        keyEvictionGrounds: [EG.NON_PAYMENT, EG.SUBLET, EG.ILLEGAL_USE, EG.NUISANCE, EG.BONA_FIDE, EG.BREACH, EG.EXPIRY],
        faqs: [
            {
                q: "Has UP adopted the Model Tenancy Act?",
                a: "Yes. UP's 2021 Act is broadly aligned with the central Model Tenancy Act 2021 — written agreement mandatory, deposit caps, Rent Authority for fast disputes.",
            },
            {
                q: "Is registration mandatory?",
                aLandlord: "Yes. Every tenancy must be in writing and submitted to the Rent Authority within 2 months. The Rent Authority issues a Unique Identification Number for each tenancy.",
                aTenant: "Yes. Insist that the landlord registers and gives you the UIN — without it, you cannot access the Rent Authority's fast-track dispute resolution.",
            },
            {
                q: "Security deposit cap?",
                a: "Maximum 2 months residential, 6 months commercial — statutory cap under the 2021 Act.",
            },
            {
                q: "Notice period?",
                a: "Per agreement. If silent, 1 month. The Act emphasizes written notice.",
            },
            {
                q: "Rent increase?",
                a: "Per your agreement. The Act doesn't cap percentage but requires 3 months' written notice before any increase.",
            },
            {
                q: "Repairs?",
                aLandlord: "Structural repairs and external systems (pipes, wiring) are the landlord's. Day-to-day inside the unit is the tenant's.",
                aTenant: "Structural and major repairs are the landlord's duty. You handle minor consumables.",
            },
            {
                q: "Eviction?",
                a: "Through the Rent Authority (fast-track, 60 days target). Grounds: non-payment of 2+ months, sub-letting, illegal use, nuisance, bona fide need, breach. No force or utility-cutting.",
            },
            {
                q: "Where to file a dispute?",
                a: "Rent Authority (district level). Appeals go to the Rent Tribunal.",
            },
        ],
        helpfulLinks: [
            { label: "UP IGRSUP (e-registration)", url: "https://igrsup.gov.in" },
        ],
    },
};

// ════════════════════════════════════════════════════════════════════
// PUBLIC API
// ════════════════════════════════════════════════════════════════════

// Look up a state's rent-law data. Falls back to GENERIC_FAQ when the
// state is missing — never returns null, so callers don't need null-checks.
export function getStateRentLaw(stateName) {
    if (!stateName) return GENERIC_FAQ;
    const key = String(stateName).trim();
    return STATES[key] || GENERIC_FAQ;
}

// Whether we have specific data for a state (UI uses this to show a
// "this is general guidance — your state's content is being added" hint).
export function isStateCovered(stateName) {
    if (!stateName) return false;
    return Object.prototype.hasOwnProperty.call(STATES, String(stateName).trim());
}

// All states the user can pick from. Includes both fully-covered states
// (top of list) and a fallback "Other state" option.
export function listStates() {
    const covered = Object.keys(STATES).sort();
    const others = [
        "Andaman & Nicobar Islands", "Arunachal Pradesh", "Assam", "Bihar",
        "Chandigarh", "Chhattisgarh", "Dadra & Nagar Haveli and Daman & Diu",
        "Goa", "Haryana", "Himachal Pradesh", "Jammu & Kashmir", "Jharkhand",
        "Ladakh", "Lakshadweep", "Madhya Pradesh", "Manipur", "Meghalaya",
        "Mizoram", "Nagaland", "Odisha", "Puducherry", "Punjab", "Rajasthan",
        "Sikkim", "Tripura", "Uttarakhand",
    ].filter(s => !covered.includes(s)).sort();
    return [...covered, ...others];
}

// Pick the right answer for the role. Falls back to `a` (shared answer)
// when the FAQ entry doesn't have a role-specific variant.
export function answerFor(faq, role) {
    if (!faq) return "";
    if (role === "tenant" && faq.aTenant) return faq.aTenant;
    if (role !== "tenant" && faq.aLandlord) return faq.aLandlord;
    return faq.a || faq.aLandlord || faq.aTenant || "";
}
